import React, { useState, useEffect, useCallback } from 'react';
import _get from 'lodash/get';

import ConfirmModal, {
  ConfirmModel,
} from '../shared/ConfirmModal/ConfirmModal';
import {
  popUpClick,
  popUpView,
  proctoringUBALogger,
} from '../../utils/trackingUtil';
import { POPUP_NAME } from '../../constants/Constants';

export default function FullScreenProctor({
  test,
  setFullScreen,
  testSolutionSet,
  fullScreenEnabled,
  enableFullScreen,
  disableFullScreen,
  webcamPermission,
  showBrowserTolerance,
  clearClipboard,
}) {
  const { is_expired, productTypeSmall } = test;

  const fullscreenPrompt = useCallback(() => {
    const fullScreenDisabled =
      document.fullscreen === false ||
      document.mozFullScreen === false ||
      document.webkitIsFullScreen === false ||
      document.fullscreenElement === null;
    if (fullScreenDisabled && !is_expired) {
      disableFullScreen();
    } else if (!fullScreenDisabled && !is_expired) {
      enableFullScreen();
    }
  }, [is_expired, disableFullScreen, enableFullScreen]);

  const disableFullScreenTracking = useCallback(() => {
    proctoringUBALogger('info', 'fullscreen disabled');
    document.removeEventListener('fullscreenchange', fullscreenPrompt, false);
    document.removeEventListener(
      'mozfullscreenchange',
      fullscreenPrompt,
      false
    );
    document.removeEventListener(
      'webkitfullscreenchange',
      fullscreenPrompt,
      false
    );
    document.removeEventListener('msfullscreenchange', fullscreenPrompt, false);
  }, [fullscreenPrompt]);

  const [modalConfig, setModalConfig] = useState(
    new ConfirmModel({
      onClose: () => {
        clearClipboard();
        setTimeout(() => {
          setFullScreen();
        }, 100);
        popUpClick({
          payload: {
            popUpTitle: POPUP_NAME.fullScreenEnabled,
            popUpAction: 'Full Screen prompt',
            label: 'Enable Full Screen',
          },
        });
        setModalConfig(config => ({ ...config, isOpen: false }));
      },
      text: `This ${productTypeSmall} requires your browser window to be in fullscreen mode.`,
      isOpen: false,
      showCancel: false,
      escapeToClose: false,
      hideHeader: true,
      centered: true,
      clickOutsideToClose: false,
      positiveBtnText: 'Enable fullscreen',
    })
  );

  const snapshot = _get(test, 'settings.proctor.snapshot');
  let proctorEnabled = _get(test, 'settings.proctor.enabled');
  const videoProctoringEnabled = _get(test, 'settings.proctor.video.enabled');

  proctorEnabled =
    (proctorEnabled || videoProctoringEnabled) && !!_get(testSolutionSet, 'id');
  const test_type_verbose = _get(test, 'test_type_verbose');

  useEffect(() => {
    if (test_type_verbose)
      setModalConfig(config => ({
        ...config,
        text: `This ${test_type_verbose} requires your browser window to be in fullscreen mode.`,
      }));
  }, [test_type_verbose]);

  // Adding fullscreen tracking event listiners
  useEffect(() => {
    const enableFullScreenTracking = () => {
      document.addEventListener('fullscreenchange', fullscreenPrompt, false);
      document.addEventListener('mozfullscreenchange', fullscreenPrompt, false);
      document.addEventListener(
        'webkitfullscreenchange',
        fullscreenPrompt,
        false
      );
      document.addEventListener('msfullscreenchange', fullscreenPrompt, false);
    };
    if (proctorEnabled) enableFullScreenTracking();
  }, [proctorEnabled, fullscreenPrompt]);

  // Removing fullscreen tracking event listiners after test has ended
  useEffect(() => {
    if (is_expired && proctorEnabled) {
      disableFullScreen();
      disableFullScreenTracking();
    }
  }, [
    proctorEnabled,
    is_expired,
    disableFullScreen,
    disableFullScreenTracking,
  ]);

  useEffect(() => {
    const fullScreenDisabled =
      document.fullscreen === false ||
      document.mozFullScreen === false ||
      document.webkitIsFullScreen === false ||
      document.fullscreenElement === null;
    if (
      proctorEnabled &&
      !fullScreenEnabled &&
      fullScreenDisabled &&
      !is_expired &&
      (!snapshot || (snapshot && webcamPermission))
    ) {
      // do not show fullscreen prompt if snapshot prompt is already shown
      setModalConfig(config => ({ ...config, isOpen: true }));
      popUpView({
        payload: {
          popUpTitle: POPUP_NAME.fullScreenEnabled,
          popUpAction: 'Full Screen prompt',
        },
      });
    } else if (
      proctorEnabled &&
      !fullScreenDisabled &&
      !fullScreenEnabled &&
      !is_expired
    ) {
      enableFullScreen();
      setModalConfig(config => ({ ...config, isOpen: false }));
    }
  }, [
    is_expired,
    fullScreenEnabled,
    proctorEnabled,
    webcamPermission,
    enableFullScreen,
    setModalConfig,
    snapshot,
  ]);

  useEffect(() => {
    if (proctorEnabled && !fullScreenEnabled && showBrowserTolerance) {
      setModalConfig(config => ({ ...config, isOpen: false }));
    }
  }, [showBrowserTolerance, fullScreenEnabled, proctorEnabled]);

  return (
    <>
      <ConfirmModal config={modalConfig} />
    </>
  );
}
